import { QuestionRegulatoryData, Questionnaire, QuestionnaireQuestion } from "../../Models/Surveys/SurveyDataModel";
import { BaseRegulationLogicManager } from "../BaseRegulationLogicManager";
import ResourcesConstants from "../ResourceConstants";
import { ComplianceScoreResult, InventoryDetailsResponse } from "../../StateControllers/Survey/SurveyResponse/SurveyResponseModels";

export class CaProp65CustomLogicManager extends BaseRegulationLogicManager {
    public static CaProp65RegulationId = "e1d5cb06-e7ce-4b11-a741-e72f96e3b739";

    private readonly ContainsChemicalKey: string = "cal_65_contains_chemical";
    private readonly RequiresDisclosureKey: string = "cal_65_requires_disclosure";
    private readonly WarningTextKey: string = "cal_65_warning_text";
    private readonly NoWarningReasonKey: string = "cal_65_no_warning_reason";
    private readonly SupplierComments: string = "cal_65_supplier_comments";
    private readonly IntendedToBeSoldInUsQuestion = "Is the product intended to be sold into the United States?";
    private readonly IntendedToBeSoldInCalifornia = "Is the product intended to be sold into the State of California?";

    constructor(questions: QuestionnaireQuestion[], overallQuestionnaire: Questionnaire, questionRegulatoryData: QuestionRegulatoryData[], selectedInventory: InventoryDetailsResponse | null, complianceScoreResult: ComplianceScoreResult | null) {
        super(questions, overallQuestionnaire, questionRegulatoryData, selectedInventory, complianceScoreResult);
    }

    protected getAdditionalQuestionIndicesToHide = (question: QuestionnaireQuestion, questionIndex: number, indicesToHideOrShow: boolean[]) => {
        if (!this.shouldShowCaProp65()) {
            this._questions.forEach(question => question.value = ''); // Clear values if not applicable
            return this._questions.map(_ => true);
        } else if (this._questions.every(q => q.hidden)) {
            indicesToHideOrShow = this._questions.map(_ => false);
        }

        // Always show Q5 (ResponseDate, index ~4), Q6 (SupplierComments, index ~5), Q7 (Files, index ~6)
        const q5Index = this._questions.findIndex(q => q.summaryFieldKey === 'cal_65_response_date') || 4;
        const q6Index = this._questions.findIndex(q => q.summaryFieldKey === this.SupplierComments) || 5;
        const q7Index = this._questions.findIndex(q => q.summaryFieldKey === 'cal_65_supporting_documentation_files') || 6;
        indicesToHideOrShow[q5Index] = false; // Always visible
        indicesToHideOrShow[q6Index] = false; // Always visible
        indicesToHideOrShow[q7Index] = false; // Always visible, override backend conditional

        // Ensure initial state: Q1 always, hide Q2-Q4 if no Q1 answer
        const q1Value = this._questions[0]?.value || '';
        if (!q1Value) {
            // Initial load: hide Q2-Q4 (indices 1-3)
            for (let i = 1; i <= 3; i++) {
                indicesToHideOrShow[i] = true;
            }
            return indicesToHideOrShow;
        }

        // Loop through all questions to apply logic for relevant ones
        this._questions.forEach((q, idx) => {
            if (q.summaryFieldKey === this.ContainsChemicalKey) { // Q1 logic
                const q2Index = this._questions.findIndex(qq => qq.summaryFieldKey === this.RequiresDisclosureKey); // Q2
                const q4Index = this._questions.findIndex(qq => qq.summaryFieldKey === this.NoWarningReasonKey); // Q4
                const q3Index = this._questions.findIndex(qq => qq.summaryFieldKey === this.WarningTextKey); // Q3

                // Q2 visible only if Q1 = Yes
                indicesToHideOrShow[q2Index] = q.value !== ResourcesConstants.NativeAnswerTexts.YesAnswer;

                // Q4: only if Q1=Yes AND Q2=No
                let requiresDisclosureValue = this._questions.find(qq => qq.summaryFieldKey === this.RequiresDisclosureKey)?.value ?? '';
                indicesToHideOrShow[q4Index] = q.value !== ResourcesConstants.NativeAnswerTexts.YesAnswer || requiresDisclosureValue !== ResourcesConstants.NativeAnswerTexts.NoAnswer;

                // Q3: Default hide unless Q2=Yes
                indicesToHideOrShow[q3Index] = true; // Hide Q3 by default
            } else if (q.summaryFieldKey === this.RequiresDisclosureKey) { // Q2 logic
                const q3Index = this._questions.findIndex(qq => qq.summaryFieldKey === this.WarningTextKey); // Q3
                const q4Index = this._questions.findIndex(qq => qq.summaryFieldKey === this.NoWarningReasonKey); // Q4

                // Get Q1 value for both Q3 and Q4 conditions
                const q1Value = this._questions.find(qq => qq.summaryFieldKey === this.ContainsChemicalKey)?.value ?? '';

                // Q3 visible only if Q1 = Yes AND Q2 = Yes
                indicesToHideOrShow[q3Index] = q1Value !== ResourcesConstants.NativeAnswerTexts.YesAnswer ||
                                              q.value !== ResourcesConstants.NativeAnswerTexts.YesAnswer;

                // Q4 visible only if Q1 = Yes AND Q2 = No
                indicesToHideOrShow[q4Index] = q1Value !== ResourcesConstants.NativeAnswerTexts.YesAnswer ||
                                              q.value !== ResourcesConstants.NativeAnswerTexts.NoAnswer;
            } else if (q.summaryFieldKey === this.NoWarningReasonKey) { // Q4 logic (for Q6 if Other, but Q6 always shown)
                // No additional hides; Q6 always visible
            }
        });
        
        
        return indicesToHideOrShow;
    }

    public customIsQuestionRequired(question: QuestionnaireQuestion) {
        // Only enforce required after responses (parents answered)
        const q1Value = this._questions.find(q => q.summaryFieldKey === this.ContainsChemicalKey)?.value || '';
        const q2Value = this._questions.find(q => q.summaryFieldKey === this.RequiresDisclosureKey)?.value || '';
        const q4Value = this._questions.find(q => q.summaryFieldKey === this.NoWarningReasonKey)?.value || '';

        if (question.summaryFieldKey === this.NoWarningReasonKey) { // Q4
            if (!q1Value || !q2Value) return false;
            return q1Value.toLowerCase() === ResourcesConstants.NativeAnswerTexts.YesAnswer.toLowerCase() &&
                   q2Value.toLowerCase() === ResourcesConstants.NativeAnswerTexts.NoAnswer.toLowerCase();
        }
        else if (question.summaryFieldKey === this.SupplierComments) { // Q6: Comments
            if (!q4Value) return false;
            return q4Value.toLowerCase() === ResourcesConstants.NativeAnswerTexts.Other.toLowerCase();
        }
        else if (question.summaryFieldKey === this.RequiresDisclosureKey) { // Q2
            return q1Value.toLowerCase() === ResourcesConstants.NativeAnswerTexts.YesAnswer.toLowerCase();
        }
        else if (question.summaryFieldKey === this.WarningTextKey) { // Q3
            return q2Value.toLowerCase() === ResourcesConstants.NativeAnswerTexts.YesAnswer.toLowerCase();
        }
        // Q5 (Date) optional
        // Q7 (Files) optional per BE

        return super.customIsQuestionRequired(question);
    }

    private shouldShowCaProp65(): boolean {
        let result = true;
        const intendedMarketsPage = this._selectedInventory?.questionnaireList.selectMany(q => q.pages).find(p => p.title == "Intended Markets");
        const intendedToBeSoldInUsQuestion = intendedMarketsPage?.questions.find(q => q.text === this.IntendedToBeSoldInUsQuestion)?.value.toLowerCase();
        const intendedToBeSoldInCalifornia = intendedMarketsPage?.questions.find(q => q.text === this.IntendedToBeSoldInCalifornia)?.value.toLowerCase();

        if (intendedToBeSoldInCalifornia === ResourcesConstants.NativeAnswerTexts.NoAnswer.toLowerCase() || intendedToBeSoldInUsQuestion === ResourcesConstants.NativeAnswerTexts.NoAnswer.toLowerCase()) {
            result = false;
        }

        return result;
    }

    public getPageRequiredInfoLocalized() {
        if (this.shouldShowCaProp65()) {
            return null;
        }

        return "California Proposition 65 data is not required because product was declared as not intended to be sold in the United States or California. If this is not correct, update Intended Markets."
    }
}
